/**
 * MCP Canvas Reveal Animation
 *
 * After a ui_render chunk completes and the iframe re-renders, this module
 * applies a staggered fade-in + quad-color glow animation to newly added components.
 * The glow matches the canvas border animation (mcp-canvas-border-animation.scss)
 * using the purple-sky-amber-pink palette (#7c3aed, #38bdf8, #f59e0b, #ec4899) — feels
 * like the AI is placing components onto the page.
 *
 * Only "root" additions are animated — components whose parent existed before
 * the batch — so nested children appear together with their parent.
 *
 * Timing: A <style> tag is injected into the iframe BEFORE the async render
 * completes, hiding new elements by ID. After the elements appear in the DOM,
 * the stagger animation runs and the hide-style is removed.
 *
 * Performance: For large batches (50+ elements), switches to a lightweight
 * bulk animation (simple fade-in + single glow) instead of per-element stagger
 * to avoid freezing the browser with hundreds of concurrent CSS animations.
 */

const REVEAL_STYLE_ID = "mcp-ai-reveal-styles";
const REVEAL_HIDE_ID = "mcp-ai-reveal-hide";
const STAGGER_DELAY_MS = 150;
const GLOW_DURATION_MS = 8000; // longest animation — cleanup must wait for this
const BULK_GLOW_DURATION_MS = 3000; // shorter glow for bulk mode
const SCROLL_THRESHOLD = 1; // only auto-scroll for single-component edits; multi-component renders stay put
const MAX_POLL_FRAMES = 40; // ~660ms at 60fps
const BULK_THRESHOLD = 50; // switch to lightweight bulk animation above this count
const MAX_STAGGER_ELEMENTS = 30; // only stagger-animate root elements, skip deep children

/** All reveal/update classes this module applies — used by the central cleanup sweep. */
const REVEAL_CLASSES: readonly string[] = [
  "mcp-ai-reveal",
  "mcp-ai-bulk-reveal",
  "mcp-ai-update",
  "mcp-ai-bulk-update"
];

/**
 * Per-element cleanup registry. Each animated element gets exactly ONE pending
 * cleanup timer, keyed by the element itself. Re-arming an element clears its
 * previous timer first, so a later reveal run can never orphan an earlier run's
 * element with a stuck class (the old shared single-timer slot did exactly that
 * for bulk mode → the root cause of the "animation replays on hover/select" bug).
 */
// Keyed by DOM node and only ever get/set/delete (never iterated), so a WeakMap
// lets a removed element be GC'd immediately instead of being pinned until its
// reveal timer fires.
const activeReveals: WeakMap<HTMLElement, ReturnType<typeof setTimeout>> = new WeakMap();

/** Monotonic counter so concurrent runs use distinct hide-style nodes. */
let revealRunCounter: number = 0;

function getIframeDoc(): Document | null {
  const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
  if (!iframe) return null;
  try {
    return iframe.contentDocument;
  } catch {
    return null; // cross-origin guard
  }
}

/**
 * Fully remove the reveal effect from an element: strip every animation class and
 * restore inline opacity (reveal sets opacity:0 before animating — never leave a
 * node stuck invisible), then drop its registry entry/timer.
 */
function clearRevealEffect(el: HTMLElement): void {
  for (const cls of REVEAL_CLASSES) {
    el.classList.remove(cls);
  }
  el.style.opacity = "";
  const timer: ReturnType<typeof setTimeout> | undefined = activeReveals.get(el);
  if (timer !== undefined) {
    clearTimeout(timer);
    activeReveals.delete(el);
  }
}

/**
 * Schedule a guaranteed cleanup for one element after `durationMs`. Re-arming
 * clears the element's previous timer first (per-element, never a shared slot),
 * so cleanup can never be skipped or clobbered by a concurrent run.
 */
function trackRevealCleanup(el: HTMLElement, durationMs: number): void {
  const existing: ReturnType<typeof setTimeout> | undefined = activeReveals.get(el);
  if (existing !== undefined) {
    clearTimeout(existing);
  }
  const timer: ReturnType<typeof setTimeout> = setTimeout(() => {
    clearRevealEffect(el);
  }, durationMs);
  activeReveals.set(el, timer);
}

/** Inject the keyframe + class definitions into the iframe <head> once. */
function ensureAnimationStyles(doc: Document): void {
  // Inject only if absent. The textContent is built from compile-time constants,
  // so there is nothing to "refresh" at runtime — and removing + recreating the
  // <style> while a concurrent run's animations reference these @keyframes would
  // tear them out mid-flight (a glitch source when many elements arrive at once).
  if (doc.getElementById(REVEAL_STYLE_ID)) return;

  const style = doc.createElement("style");
  style.id = REVEAL_STYLE_ID;
  style.textContent = `
/* 1. Clip-path wipe: reveals component top-to-bottom like AI is constructing it */
@keyframes mcp-ai-wipe-in {
  from {
    clip-path: inset(0 0 100% 0);
  }
  to {
    clip-path: inset(0 0 0 0);
  }
}

/* 2. Ambient purple-sky-amber-pink glow (box-shadow) — matches canvas border animation */
@keyframes mcp-ai-purple-glow {
  0% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 0 0 rgba(56,189,248, 0),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
  15% {
    box-shadow:
      0 0 35px 8px rgba(124,58,237, 0.3),
      0 0 60px 18px rgba(56,189,248, 0.12),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
  35% {
    box-shadow:
      0 0 15px 4px rgba(124,58,237, 0.08),
      0 0 40px 12px rgba(56,189,248, 0.3),
      0 0 60px 18px rgba(245,158,11, 0.12),
      0 0 0 0 rgba(236,72,153, 0);
  }
  55% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 15px 4px rgba(56,189,248, 0.08),
      0 0 40px 12px rgba(245,158,11, 0.28),
      0 0 60px 18px rgba(236,72,153, 0.12);
  }
  75% {
    box-shadow:
      0 0 20px 6px rgba(124,58,237, 0.18),
      0 0 0 0 rgba(56,189,248, 0),
      0 0 15px 4px rgba(245,158,11, 0.08),
      0 0 45px 14px rgba(236,72,153, 0.25);
  }
  100% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 0 0 rgba(56,189,248, 0),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
}

/* 3. Shimmer sweep: tricolor light passes left-to-right */
@keyframes mcp-ai-shimmer-sweep {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}

/* 4. Diagonal glass shine: bright highlight bar sweeps across */
@keyframes mcp-ai-shine {
  0% {
    transform: translateX(-100%) skewX(-15deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateX(200%) skewX(-15deg);
    opacity: 0;
  }
}

/* 5. Lightweight bulk fade-in for large batches */
@keyframes mcp-ai-bulk-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* 6. Lightweight bulk glow — single short pulse cycling purple → sky → amber → pink */
@keyframes mcp-ai-bulk-glow {
  0% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 0 0 rgba(56,189,248, 0),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
  25% {
    box-shadow:
      0 0 18px 5px rgba(124,58,237, 0.22),
      0 0 22px 6px rgba(56,189,248, 0.14),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
  55% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 10px 3px rgba(56,189,248, 0.08),
      0 0 22px 6px rgba(245,158,11, 0.2),
      0 0 18px 5px rgba(236,72,153, 0.14);
  }
  100% {
    box-shadow:
      0 0 0 0 rgba(124,58,237, 0),
      0 0 0 0 rgba(56,189,248, 0),
      0 0 0 0 rgba(245,158,11, 0),
      0 0 0 0 rgba(236,72,153, 0);
  }
}

.mcp-ai-reveal {
  position: relative;
  overflow: hidden;
  animation:
    mcp-ai-wipe-in 3s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards,
    mcp-ai-purple-glow ${GLOW_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: inherit;
  will-change: clip-path, box-shadow;
}

/* Shimmer overlay — yellow-blue-purple light sweeps across during reveal */
.mcp-ai-reveal::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(124, 58, 237, 0.12) 12%,
    rgba(56, 189, 248, 0.18) 30%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(245, 158, 11, 0.18) 70%,
    rgba(236, 72, 153, 0.12) 88%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: mcp-ai-shimmer-sweep 3.5s cubic-bezier(0.4, 0, 0.2, 1) 0.6s;
}

/* Diagonal glass shine — bright highlight bar */
.mcp-ai-reveal::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 40%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.03) 30%,
    rgba(255, 255, 255, 0.10) 50%,
    rgba(255, 255, 255, 0.03) 70%,
    transparent 100%
  );
  transform: translateX(-100%) skewX(-15deg);
  animation: mcp-ai-shine 4s cubic-bezier(0.4, 0, 0.2, 1) 1.2s;
}

/* ── Bulk reveal: lightweight fade + single glow, NO pseudo-element animations ── */
.mcp-ai-bulk-reveal {
  animation:
    mcp-ai-bulk-fade 0.6s ease-out forwards,
    mcp-ai-bulk-glow ${BULK_GLOW_DURATION_MS}ms ease-out;
  border-radius: inherit;
}

/* ── Update highlight: glow + shimmer without wipe-in (for existing components) ── */
.mcp-ai-update {
  position: relative;
  overflow: hidden;
  animation: mcp-ai-purple-glow ${GLOW_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: inherit;
  will-change: box-shadow;
}

.mcp-ai-update::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 9998;
  pointer-events: none;
  border-radius: inherit;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(124, 58, 237, 0.12) 12%,
    rgba(56, 189, 248, 0.18) 30%,
    rgba(255, 255, 255, 0.12) 50%,
    rgba(245, 158, 11, 0.18) 70%,
    rgba(236, 72, 153, 0.12) 88%,
    transparent 100%
  );
  transform: translateX(-100%);
  animation: mcp-ai-shimmer-sweep 3.5s cubic-bezier(0.4, 0, 0.2, 1) 0.4s;
}

/* Diagonal glass shine — bright highlight bar */
.mcp-ai-update::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 40%;
  height: 100%;
  z-index: 9999;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.03) 30%,
    rgba(255, 255, 255, 0.10) 50%,
    rgba(255, 255, 255, 0.03) 70%,
    transparent 100%
  );
  transform: translateX(-100%) skewX(-15deg);
  animation: mcp-ai-shine 4s cubic-bezier(0.4, 0, 0.2, 1) 0.8s;
}

/* ── Bulk update: lightweight glow only ── */
.mcp-ai-bulk-update {
  animation: mcp-ai-bulk-glow ${BULK_GLOW_DURATION_MS}ms ease-out;
  border-radius: inherit;
}

@media (prefers-reduced-motion: reduce) {
  .mcp-ai-reveal,
  .mcp-ai-reveal::before,
  .mcp-ai-reveal::after,
  .mcp-ai-update,
  .mcp-ai-update::before,
  .mcp-ai-update::after,
  .mcp-ai-bulk-reveal,
  .mcp-ai-bulk-update {
    animation: none !important;
    clip-path: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
}
`;
  doc.head.appendChild(style);
}

/**
 * Inject a <style> that hides all new elements by kuikaId BEFORE the render
 * completes. This prevents a flash of unstyled content.
 */
function injectHideStyle(doc: Document, kuikaIds: string[], hideId: string): void {
  doc.getElementById(hideId)?.remove();
  if (kuikaIds.length === 0) return;

  const style: HTMLStyleElement = doc.createElement("style");
  style.id = hideId;
  style.textContent =
    kuikaIds.map((id) => `[kuikaid="${id}"]`).join(",") + " { opacity: 0 !important; transition: none !important; }";
  doc.head.appendChild(style);
}

function removeHideStyle(doc: Document, hideId: string): void {
  doc.getElementById(hideId)?.remove();
}

/**
 * Identify "root" additions: elements whose nearest kuikaid ancestor
 * is NOT in the new-ids set (i.e., the parent existed before this batch).
 */
function findRootElements(doc: Document, newIds: Set<string>): HTMLElement[] {
  const roots: HTMLElement[] = [];

  for (const id of newIds) {
    const el = doc.querySelector(`[kuikaid="${id}"]`) as HTMLElement | null;
    if (!el) continue;

    let parent = el.parentElement;
    let isRoot = true;

    while (parent) {
      const parentKuikaId = parent.getAttribute("kuikaid");
      if (parentKuikaId) {
        if (newIds.has(parentKuikaId)) {
          isRoot = false;
        }
        break;
      }
      parent = parent.parentElement;
    }

    if (isRoot) {
      roots.push(el);
    }
  }

  return roots;
}

/**
 * Collect direct new children (with kuikaIds in newIds) of a given element.
 * Walks the DOM tree shallowly — stops at the first kuikaId match per branch.
 */
function collectNewChildren(el: HTMLElement, newIds: Set<string>): HTMLElement[] {
  const children: HTMLElement[] = [];
  for (const child of Array.from(el.children)) {
    if (!(child instanceof HTMLElement)) continue;
    const childKuikaId = child.getAttribute("kuikaid");
    if (childKuikaId && newIds.has(childKuikaId)) {
      children.push(child);
    } else {
      // Structural wrapper without kuikaid (or non-new) — look inside
      children.push(...collectNewChildren(child, newIds));
    }
  }
  return children;
}

/**
 * Collect ALL new elements in document (tree) order.
 * Uses a single querySelectorAll with a combined selector for O(1) DOM queries
 * instead of N individual querySelector calls.
 */
function collectAllNewElementsInTreeOrder(doc: Document, newIds: Set<string>): HTMLElement[] {
  if (newIds.size === 0) return [];

  // Build a single combined CSS selector — querySelectorAll returns in document order
  const selector = Array.from(newIds)
    .map((id) => `[kuikaid="${id}"]`)
    .join(",");
  const nodeList = doc.querySelectorAll<HTMLElement>(selector);

  // querySelectorAll already returns in document order — no sort needed
  return Array.from(nodeList);
}

/**
 * Collect only root-level new elements in document order (for stagger animation).
 * Uses a single DOM query then filters to roots.
 */
function collectRootElementsInTreeOrder(doc: Document, newIds: Set<string>): HTMLElement[] {
  if (newIds.size === 0) return [];

  const selector = Array.from(newIds)
    .map((id) => `[kuikaid="${id}"]`)
    .join(",");
  const nodeList = doc.querySelectorAll<HTMLElement>(selector);
  const roots: HTMLElement[] = [];

  for (const el of nodeList) {
    let parent = el.parentElement;
    let isRoot = true;

    while (parent) {
      const parentKuikaId = parent.getAttribute("kuikaid");
      if (parentKuikaId) {
        if (newIds.has(parentKuikaId)) {
          isRoot = false;
        }
        break;
      }
      parent = parent.parentElement;
    }

    if (isRoot) {
      roots.push(el);
    }
  }

  return roots;
}

/**
 * Scroll the kuika_scrollFrame inside the iframe to bring the element into view.
 */
function scrollToElement(doc: Document, el: HTMLElement): void {
  const scrollFrame = doc.getElementById("kuika_scrollFrame");
  if (!scrollFrame) return;

  const frameRect = scrollFrame.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();

  // Only scroll if the element is outside the visible area
  const isAbove = elRect.top < frameRect.top;
  const isBelow = elRect.bottom > frameRect.bottom;

  if (isAbove || isBelow) {
    // Calculate scroll target: center the element in the visible area
    const elCenter = elRect.top + elRect.height / 2;
    const frameCenter = frameRect.top + frameRect.height / 2;
    const scrollDelta = elCenter - frameCenter;

    scrollFrame.scrollBy({
      top: scrollDelta,
      behavior: "smooth"
    });
  }
}

/**
 * Recursively collect all kuikaIds from a newComponent result object.
 */
function collectKuikaIds(component: any, out: string[]): void {
  if (component?.kuikaId) out.push(String(component.kuikaId));
  if (component?.children && Array.isArray(component.children)) {
    for (const child of component.children) {
      collectKuikaIds(child, out);
    }
  }
}

/**
 * Extract all new kuikaIds from a ui_component.add tool result.
 */
export function extractNewKuikaIds(result: any): string[] {
  const ids: string[] = [];
  if (!result || result.error) return ids;

  const results = result.results;
  if (!Array.isArray(results)) return ids;

  for (const r of results) {
    if (!r.success) continue;
    collectKuikaIds(r.newComponent, ids);
    // Also track auto-wrapped Col
    if (r.colKuikaId) ids.push(String(r.colKuikaId));
  }

  return ids;
}

/**
 * Bulk reveal: apply a single lightweight class to ALL elements at once.
 * No stagger, no pseudo-element animations. Each element gets its OWN registry-keyed
 * cleanup, so a later bulk run can never strand an earlier run's elements with a
 * stuck `.mcp-ai-bulk-reveal` class (the old shared single-timer slot did exactly
 * that → the class lingered and replayed on the next iframe re-render).
 */
function bulkReveal(elements: HTMLElement[]): void {
  // Show all elements at once — single reflow
  for (const el of elements) {
    clearRevealEffect(el); // sweep any stale class/timer from a prior run on this node
    el.style.opacity = "";
    el.classList.add("mcp-ai-bulk-reveal");
    trackRevealCleanup(el, BULK_GLOW_DURATION_MS + 200);
  }
}

/**
 * Bulk update highlight: apply lightweight glow class to all elements at once.
 */
function bulkUpdateHighlight(elements: HTMLElement[]): void {
  for (const el of elements) {
    el.classList.remove("mcp-ai-bulk-update");
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    void el.offsetWidth; // force reflow to restart animation
    el.classList.add("mcp-ai-bulk-update");
    trackRevealCleanup(el, BULK_GLOW_DURATION_MS + 200);
  }
}

/**
 * Stagger-animate a small number of elements with the full reveal effect.
 * Caps the number of individually animated elements to MAX_STAGGER_ELEMENTS
 * by only animating root elements (children appear with their parent).
 */
function staggerReveal(doc: Document, allElements: HTMLElement[], newIds: Set<string>): void {
  // Only stagger root elements to cap the number of timers/animations
  const roots = collectRootElementsInTreeOrder(doc, newIds);
  const animateList = roots.length > MAX_STAGGER_ELEMENTS ? roots.slice(0, MAX_STAGGER_ELEMENTS) : roots;
  const animateSet = new Set(animateList);

  // Non-root elements (or overflow roots): reveal immediately without animation
  for (const el of allElements) {
    if (!animateSet.has(el)) {
      el.style.opacity = "";
    }
  }

  // Stagger-animate the capped root elements
  const enableScroll = animateList.length <= SCROLL_THRESHOLD;
  animateList.forEach((el, i) => {
    setTimeout(() => {
      if (enableScroll) scrollToElement(doc, el);

      clearRevealEffect(el); // sweep any stale class/timer from a prior run on this node
      el.style.opacity = "";
      el.classList.add("mcp-ai-reveal");

      // Single guaranteed cleanup (registry-keyed) covering the longest animation.
      // Routing through the registry means a later run can never orphan this node,
      // so the class is always gone once the reveal ends — nothing left to replay.
      trackRevealCleanup(el, GLOW_DURATION_MS + 200);
    }, i * STAGGER_DELAY_MS);
  });
}

/**
 * Schedule a staggered reveal animation for newly added components.
 * Call this right after the last tool in a ui_render batch returns
 * (before the async render completes).
 *
 * Performance: For 50+ elements, uses a lightweight bulk animation
 * (simple fade-in + single glow pulse) instead of per-element stagger
 * with 4 concurrent CSS animations each.
 */
export function scheduleStaggerReveal(kuikaIds: string[]): void {
  if (!kuikaIds || kuikaIds.length === 0) return;

  const doc = getIframeDoc();
  if (!doc) {
    return;
  }

  ensureAnimationStyles(doc);
  // Per-run hide node: concurrent runs must not remove each other's hide style.
  const hideId: string = `${REVEAL_HIDE_ID}-${++revealRunCounter}`;
  injectHideStyle(doc, kuikaIds, hideId);

  const newIds = new Set(kuikaIds);
  const isBulk = newIds.size >= BULK_THRESHOLD;
  let attempts = 0;

  const poll = () => {
    attempts++;

    // Spot-check a few IDs instead of iterating the entire set
    const idsArray = Array.from(newIds);
    const checkIndices = [0, Math.floor(idsArray.length / 2), idsArray.length - 1];
    const anyFound = checkIndices.some((idx) => {
      const id = idsArray[idx];
      return id ? !!doc.querySelector(`[kuikaid="${id}"]`) : false;
    });

    if (!anyFound && attempts < MAX_POLL_FRAMES) {
      requestAnimationFrame(poll);
      return;
    }

    if (!anyFound) {
      removeHideStyle(doc, hideId);
      return;
    }

    // Single batched DOM query for all elements
    const allElements = collectAllNewElementsInTreeOrder(doc, newIds);

    if (allElements.length === 0) {
      removeHideStyle(doc, hideId);
      return;
    }

    // Set inline opacity on all elements before removing the global hide style
    for (const el of allElements) {
      el.style.opacity = "0";
    }
    removeHideStyle(doc, hideId);

    if (isBulk || allElements.length >= BULK_THRESHOLD) {
      // Bulk mode: single class toggle, no stagger, no per-element timers
      bulkReveal(allElements);
    } else {
      // Normal mode: stagger root elements with full animation
      staggerReveal(doc, allElements, newIds);
    }
  };

  // Start polling on the next frame (render is async)
  requestAnimationFrame(poll);
}

/**
 * Schedule a highlight animation for existing components that were updated (styled/configured).
 * Unlike scheduleStaggerReveal, this does NOT hide the elements — just applies the
 * glow + shimmer effect to show the AI touched them.
 *
 * Performance: For 50+ elements, uses lightweight bulk glow.
 */
export function scheduleUpdateHighlight(kuikaIds: string[]): void {
  if (!kuikaIds || kuikaIds.length === 0) return;

  const doc = getIframeDoc();
  if (!doc) {
    return;
  }

  ensureAnimationStyles(doc);

  const targetIds = new Set(kuikaIds);
  const isBulk = targetIds.size >= BULK_THRESHOLD;
  let attempts = 0;

  const poll = () => {
    attempts++;

    // Single batched DOM query
    const selector = Array.from(targetIds)
      .map((id) => `[kuikaid="${id}"]`)
      .join(",");
    const elements = Array.from(doc.querySelectorAll<HTMLElement>(selector));

    if (elements.length === 0 && attempts < MAX_POLL_FRAMES) {
      requestAnimationFrame(poll);
      return;
    }

    if (elements.length === 0) {
      return;
    }

    if (isBulk || elements.length >= BULK_THRESHOLD) {
      // Bulk mode: single lightweight glow on all at once
      bulkUpdateHighlight(elements);
    } else {
      // Normal stagger mode
      const enableScroll = elements.length <= SCROLL_THRESHOLD;
      elements.forEach((el, i) => {
        setTimeout(() => {
          if (enableScroll) scrollToElement(doc, el);

          // Remove any stale class/timer first, then force reflow so re-adding
          // the class restarts the animation cleanly.
          clearRevealEffect(el);
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          void el.offsetWidth;
          el.classList.add("mcp-ai-update");

          // Single guaranteed cleanup (registry-keyed) covering the longest animation.
          trackRevealCleanup(el, GLOW_DURATION_MS + 200);
        }, i * STAGGER_DELAY_MS);
      });
    }
  };

  requestAnimationFrame(poll);
}
